import React from 'react';
import {
  getLinkedOrganizationsPending,
  getLinkedOrganizationsSuccess,
  getLinkedOrganizationsError,
  setBannerState,
  getLinkedOrganizationGroupsPending,
  getLinkedOrganizationGroupsSuccess,
  getLinkedOrganizationGroupsError,
  claimLinkedOrganizationAdminPending,
  claimLinkedOrganizationAdminSuccess,
  claimLinkedOrganizationAdminError,
  addUserToOrganizationGroupPending,
  addUserToOrganizationGroupSuccess,
  addUserToOrganizationGroupError,
  removeUserFromOrganizationGroupConfirm,
  removeUserFromOrganizationGroupPending,
  removeUserFromOrganizationGroupSuccess,
  removeUserFromOrganizationGroupError,
  unlinkOrganizationPending,
  unlinkOrganizationSuccess,
  unlinkOrganizationError,
  toggleProcessedItem,
  toggleModal,
  toggleForm,
  triggerModal,
  revalidateOrgLinkWithPinPending,
  revalidateOrgLinkWithPinSuccess,
  revalidateOrgLinkWithPinError
} from '../actions/AppActions';
import { createLogic } from 'redux-logic';
import { formatAccounts, formatGroups } from '../utils/profileHelpers';
import intl from '../actions/lang/en.json';
import { PATH_PROFILE, UNLINK_ORG_CONFIRM } from '../constants';
import history from '../utils/history';

export const getOrganizationsLogic = createLogic({
  type: getLinkedOrganizationsPending().type,
  latest: true,
  process({ httpClient, action }, dispatch, done) {
    const { userId } = action?.payload;

    return httpClient(dispatch).get('/organizations')
      .then(res => {
        dispatch(getLinkedOrganizationsSuccess(formatAccounts(formatGroups(res.data, userId))));
        done();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_Linked_Orgs_Error } }));
        dispatch(getLinkedOrganizationsError(error));
        done();
      });
  }
});

export const getOrganizationGroupsLogic = createLogic({
  type: getLinkedOrganizationGroupsPending().type,
  latest: true,

  process({ httpClient, action }, dispatch, done) {
    const { organizationId } = action?.payload;

    return httpClient(dispatch).get(`/organizations/${organizationId}/groups`)
    .then(resp => {
      dispatch(getLinkedOrganizationGroupsSuccess({
        organizationId,
        groups: resp.data
      }));
      done();
    }).catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Get_Linked_Org_Groups_Error } }));
      dispatch(getLinkedOrganizationGroupsError(error));
      done();
    });
  }
});

export const unlinkOrganizationConfirmationLogic = createLogic({
  type: UNLINK_ORG_CONFIRM,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },
  process({ action }, dispatch) {
    const { organizationName, organizationId, userCanUnlink } = action.payload;
    const unlinkMessage = `You are about to unlink ${organizationName}. You will no longer be able to make purchases on behalf of this organization or view this profile. Do you want to unlink?`;
    const addAdminMessage = 'You must add another administrator before you can unlink this organization.';
    const modalProps = userCanUnlink
      ? {
        show: true,
        message: unlinkMessage,
        okLabel: 'Yes',
        okDataHook: 'unlinkOrg.yes',
        cancelLabel: 'No',
        cancelDataHook: 'unlinkOrg.no',
        buttonOrder: 'rtl',
        okClick: () => {
          dispatch(toggleProcessedItem(organizationId));
          dispatch(unlinkOrganizationPending(organizationId));
          dispatch(toggleModal());
        },
        cancelClick: () => {
          history.push(PATH_PROFILE);
          dispatch(toggleModal());
        }
      }
      : {
        show: true,
        message: addAdminMessage,
        okLabel: 'Go to Groups',
        okDataHook: 'unlinkOrg.goToGroups',
        cancelLabel: 'Go Back',
        cancelDataHook: 'unlinkOrg.goBack',
        buttonOrder: 'rtl',
        okClick: () => {
          history.push(`/organization/${organizationId}/groups`);
          dispatch(toggleModal());
        },
        cancelClick: () => {
          history.push(PATH_PROFILE);
          dispatch(toggleModal());
        }
      };
    dispatch(toggleForm(null));
    dispatch(triggerModal(modalProps));
  }
});

export const unlinkOrganizationLogic = createLogic({
  type: unlinkOrganizationPending().type,
  latest: true,
  process({ httpClient, action, getState}, dispatch, done) {
    const { callbackFn, payload } = action;
    const organizationId = payload?.organizationId;
    return httpClient(dispatch)
      .delete(`/organizations/${organizationId}`)
      .then(() => {
        dispatch(unlinkOrganizationSuccess(organizationId));
        if (callbackFn) callbackFn();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Unlink_Organization_Error } }));
        dispatch(unlinkOrganizationError(error));
      })
      .finally(() => done());
  }
});

export const claimOrganizationAdminLogic = createLogic({
  type: claimLinkedOrganizationAdminPending().type,
  latest: true,

  process({ httpClient, action }, dispatch, done) {
    const { organizationId, errorCallbackFn, bannerLocation } = action?.payload;

    return httpClient(dispatch).post(`/organizations/${organizationId}/admin`, {})
    .then(({ data }) => {
      const { adminGroupId } =data?.data;
      dispatch(getLinkedOrganizationGroupsPending(organizationId));
      dispatch(claimLinkedOrganizationAdminSuccess({ organizationId, adminGroupId }));
      done();
    })
    .catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Claim_Linked_Org_Admin_Error, location: (bannerLocation || '') } }));
      dispatch(claimLinkedOrganizationAdminError(error));
      if (errorCallbackFn) errorCallbackFn();
      done();
    });
  }
});

export const addUserToOrganizationGroupLogic = createLogic({
  type: addUserToOrganizationGroupPending().type,
  latest: true,

  process({ httpClient, action }, dispatch, done) {
    const { email, organizationId, groupId, errorCallbackFn, successCallbackFn, bannerLocation } = action?.payload;

    return httpClient(dispatch).post(`/organizations/${organizationId}/groups/${groupId}/users`, { email })
      .then(resp => {
        dispatch(addUserToOrganizationGroupSuccess(resp.data));
        dispatch(getLinkedOrganizationGroupsPending(organizationId));
        if (successCallbackFn) successCallbackFn();
        done();
      })
      .catch(error => {
        let errorMessage;
        if (error?.response?.data?.errorCode === 'email.username.not.found') {
         errorMessage = intl.Errors.Organizations.Add_User_To_Group_Missing_LifeWay_Account_Error;
        } else if (error?.response?.data?.errorCode === 'duplicate.member') {
         errorMessage = intl.Errors.Organizations.Add_User_To_Group_Duplicate_Member_Error;
        } else {
         errorMessage = intl.Errors.Organizations.Add_User_To_Group_Error;
        }

        dispatch(setBannerState({ data: { type: 'error', message: errorMessage.replace(':email', email), location: (bannerLocation || '') } }));
        dispatch(addUserToOrganizationGroupError(error));
        if (errorCallbackFn) errorCallbackFn();
        done();
      });
  }
});

export const removeUserFromOrganizationGroupConfirmationLogic = createLogic({
  type: removeUserFromOrganizationGroupConfirm().type,
  latest: true,
  processOptions: {
    dispatchMultiple: true,
  },

  process({ action }, dispatch) {
    const { organizationId, groupId, personId, name, email, bannerLocation, userCannotRemoveMember, openAddMembersForm, successCallbackFn } = action?.payload;

    const MessageCmpt = () => (
      <span>
        {userCannotRemoveMember ?
          <>You must add another adminstrator before you can remove {name}.</>
        : <>You are about to remove {name} ({email}) from this group.<br/>Are you sure?</>
        }
      </span>
  );

    const modalProps = userCannotRemoveMember
    ? {
        show: true,
        message: <MessageCmpt />,
        okLabel: 'Add Administrator',
        okDataHook: 'removeUserGroup.goBack',
        buttonOrder: 'rtl',
        okClick: () => {
          dispatch(toggleModal());
          dispatch(openAddMembersForm());
        }
      }
    : {
        show: true,
        message: <MessageCmpt />,
        okLabel: 'Yes',
        okDataHook: 'removeUserGroup.yes',
        cancelLabel: 'No',
        cancelDataHook: 'removeUserGroup.no',
        buttonOrder: 'rtl',
        okClick: () => {
          dispatch(removeUserFromOrganizationGroupPending({ organizationId, groupId, personId, email, bannerLocation, successCallbackFn }));
          dispatch(toggleModal());
        },
        cancelClick: () => {
          dispatch(toggleModal());
        }
    };
    dispatch(triggerModal(modalProps));
  }
});

export const removeUserFromOrganizationGroupLogic = createLogic({
  type: removeUserFromOrganizationGroupPending().type,
  latest: true,

  process({ httpClient, action }, dispatch, done) {
    const { organizationId, groupId, personId } = action?.payload;
    const { email, bannerLocation, successCallbackFn } = action?.payload;

    return httpClient(dispatch).delete(`/organizations/${organizationId}/groups/${groupId}/users/${personId}`)
      .then(resp => {
        dispatch(removeUserFromOrganizationGroupSuccess({ personId, organizationId, groupId }));
        dispatch(getLinkedOrganizationGroupsPending(organizationId));
        if (successCallbackFn) successCallbackFn();
      })
      .catch(error => {
        dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Remove_User_From_Group_Error.replace(':email', email), location: (bannerLocation || '') } }));
        dispatch(removeUserFromOrganizationGroupError(error));
      }).finally(() => done());
  }
});

export const relinkBillingAccountLogic = createLogic({
  type: revalidateOrgLinkWithPinPending().type,
  latest: true,
  process({ httpClient, action: { payload } }, dispatch, done) {
    const { accountNumber, pin, id, errorCallback } = payload;
    httpClient(dispatch).put('/linked-billing-accounts',
      { accountNumber, pin, billingAccountLinkId: id }
    ).then(res => {
      dispatch(revalidateOrgLinkWithPinSuccess(res.data));
    }).catch(error => {
      dispatch(setBannerState({ data: { type: 'error', message: intl.Errors.Organizations.Revalidate_Pin_Error } }));
      dispatch(revalidateOrgLinkWithPinError(error));
      errorCallback && errorCallback(intl.Errors.Organizations.Revalidate_Pin_Error);
    }).finally(() => done());
  }
});

export default [
  getOrganizationsLogic,
  getOrganizationGroupsLogic,
  claimOrganizationAdminLogic,
  addUserToOrganizationGroupLogic,
  removeUserFromOrganizationGroupConfirmationLogic,
  removeUserFromOrganizationGroupLogic,
  unlinkOrganizationConfirmationLogic,
  unlinkOrganizationLogic,
  relinkBillingAccountLogic
];
